import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createTenant } from '../../../services/api';
import './AddTenant.css';

// Interface para o formulário
interface TenantFormData {
  name: string;
  email: string;
  phone: string;
  document: string;
  address: string;
  initialCredits: string;
  active: boolean;
  initialUser: {
    name: string;
    username: string;
    email: string;
    password: string;
    confirmPassword: string;
  };
}

const AddTenant: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<TenantFormData>({
    name: '',
    email: '',
    phone: '',
    document: '',
    address: '',
    initialCredits: '0',
    active: true,
    initialUser: {
      name: '',
      username: '',
      email: '',
      password: '',
      confirmPassword: ''
    }
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleUserInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      initialUser: {
        ...prev.initialUser,
        [name]: value
      }
    }));
  };

  const validateForm = (): boolean => {
    // Validação básica
    if (!formData.name || !formData.email) {
      setError('Nome e email do cliente são obrigatórios');
      return false;
    }
    
    if (!formData.initialUser.name || !formData.initialUser.username || 
        !formData.initialUser.email || !formData.initialUser.password) {
      setError('Todos os dados do usuário inicial são obrigatórios');
      return false;
    }
    
    if (formData.initialUser.password !== formData.initialUser.confirmPassword) {
      setError('As senhas não conferem');
      return false;
    }
    
    // Validar e-mails
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Email do cliente inválido');
      return false;
    }
    
    if (!emailRegex.test(formData.initialUser.email)) {
      setError('Email do usuário inicial inválido');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Preparar dados para API
      const apiData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        initialCredits: parseFloat(formData.initialCredits) || 0,
        active: formData.active,
        initialUser: {
          name: formData.initialUser.name,
          username: formData.initialUser.username,
          email: formData.initialUser.email,
          password: formData.initialUser.password
        }
      };
      
      // Chamada à API real
      const response = await createTenant(apiData);
      
      if (response.success) {
        navigate('/admin/tenants');
      } else {
        setError(response.error || 'Erro ao criar cliente');
        // Se houver detalhes específicos do erro da API
        if (response.details && response.details.message) {
          setError(response.details.message);
        }
      }
    } catch (err) {
      console.error('Erro ao criar cliente:', err);
      setError('Erro ao processar a solicitação. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-tenant-container">
      <div className="form-header">
        <Link to="/admin/tenants" className="btn-back">
          &larr; Voltar para lista de clientes
        </Link>
        <h2>Adicionar Novo Cliente</h2>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <form onSubmit={handleSubmit} className="tenant-form">
        <div className="form-section">
          <h3>Informações do Cliente</h3>
          
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name">Nome da Empresa*</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Digite o nome da empresa"
                required
                disabled={loading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email*</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="email@empresa.com"
                required
                disabled={loading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">Telefone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="(00) 0000-0000"
                disabled={loading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="document">CNPJ</label>
              <input
                type="text"
                id="document"
                name="document"
                value={formData.document}
                onChange={handleInputChange}
                placeholder="00.000.000/0000-00"
                disabled={loading}
              />
            </div>
            
            <div className="form-group full-width">
              <label htmlFor="address">Endereço</label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Endereço completo"
                disabled={loading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="initialCredits">Créditos Iniciais</label>
              <input
                type="number"
                id="initialCredits"
                name="initialCredits"
                value={formData.initialCredits}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                placeholder="0.00"
                disabled={loading}
              />
            </div>
            
            <div className="form-group checkbox-group">
              <label htmlFor="active">
                <input
                  type="checkbox"
                  id="active"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                Cliente Ativo
              </label>
            </div>
          </div>
        </div>
        
        <div className="form-section">
          <h3>Usuário Inicial</h3>
          <p className="section-description">
            Crie um usuário administrador para este cliente. Este usuário poderá acessar o sistema.
          </p>
          
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="initialUserName">Nome*</label>
              <input
                type="text"
                id="initialUserName"
                name="name"
                value={formData.initialUser.name}
                onChange={handleUserInputChange}
                placeholder="Nome completo"
                required
                disabled={loading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="initialUserUsername">Nome de Usuário*</label>
              <input
                type="text"
                id="initialUserUsername"
                name="username"
                value={formData.initialUser.username}
                onChange={handleUserInputChange}
                placeholder="username"
                required
                disabled={loading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="initialUserEmail">Email*</label>
              <input
                type="email"
                id="initialUserEmail"
                name="email"
                value={formData.initialUser.email}
                onChange={handleUserInputChange}
                placeholder="email@usuario.com"
                required
                disabled={loading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="initialUserPassword">Senha*</label>
              <input
                type="password"
                id="initialUserPassword"
                name="password"
                value={formData.initialUser.password}
                onChange={handleUserInputChange}
                placeholder="********"
                required
                disabled={loading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="initialUserConfirmPassword">Confirmar Senha*</label>
              <input
                type="password"
                id="initialUserConfirmPassword"
                name="confirmPassword"
                value={formData.initialUser.confirmPassword}
                onChange={handleUserInputChange}
                placeholder="********"
                required
                disabled={loading}
              />
            </div>
          </div>
        </div>
        
        <div className="form-actions">
          <button 
            type="button" 
            className="btn-cancel" 
            onClick={() => navigate('/admin/tenants')}
            disabled={loading}
          >
            Cancelar
          </button>
          <button 
            type="submit" 
            className="btn-save"
            disabled={loading}
          >
            {loading ? 'Salvando...' : 'Criar Cliente'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddTenant; 